import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { ListWaitingReceiptsInput } from "@nexxt/contracts/procurement";
import { inboundQueries } from "@nexxt/procurement-ui";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of what the warehouse received (I-11, ADR-138): the receipts of an inbound, under the keys of the
// inbound, so whatever reloads the inbounds reloads them too; and the receipts that wait, under the inbounds as well,
// since applying one changes an inbound.

const procurement = api.procurement;

export type Receipt = InferResponseType<(typeof procurement.inbounds)[":inboundId"]["receipts"]["$get"], 200>[number];
type WaitingPage = InferResponseType<(typeof procurement)["waiting-receipts"]["$get"], 200>;
export type WaitingReceipt = WaitingPage["items"][number];

/** The filters of the list input (`listWaitingReceiptsInput`), without the paging the queries add. */
export type WaitingReceiptFilters = Pick<ListWaitingReceiptsInput, "reason">;

const PAGE_SIZE = 50;

const keys = {
  /** Prefix of every cached list of the receipts that wait. */
  waiting: [...inboundQueries.all, "waiting-receipts"] as const,
};

export const receiptQueries = {
  ...keys,

  ofInbound: (inboundId: string) =>
    queryOptions({
      queryKey: [...inboundQueries.of(inboundId), "receipts"],
      queryFn: ({ signal }) =>
        unwrap(procurement.inbounds[":inboundId"].receipts.$get({ param: { inboundId } }, { init: { signal } })),
    }),

  waiting: (filters: WaitingReceiptFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.waiting, filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          procurement["waiting-receipts"].$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),
};
